import {
	AlertOutlined,
	ClockCircleOutlined,
	CreditCardOutlined,
	MessageOutlined,
	PlusOutlined,
	UserAddOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Select, Spin } from "antd";
import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import type { AppRole } from "@/shared/config/navigation";
import { BarChart } from "../components/BarChart";
import { KpiCard } from "../components/KpiCard";
import { LineChart } from "../components/LineChart";
import { ManagersCard } from "../components/ManagersCard";
import { PaymentDeadlinesCard } from "../components/PaymentDeadlinesCard";
import { RecentSalesCard } from "../components/RecentSalesCard";
import { RoomsStatusDonut } from "../components/RoomsStatusDonut";
import { dashboardApi } from "../services/dashboard.api";

export function DashboardPage() {
	const navigate = useNavigate();
	const { role } = useOutletContext<{ role: AppRole }>();
	const isSuperAdmin = role === "super";
	const [chartPeriod, setChartPeriod] = useState<"Yil" | "Oy">("Yil");
	const [selectedBuildingId, setSelectedBuildingId] = useState<string>("all");
	const [selectedOrgId, setSelectedOrgId] = useState<string>("all");

	const { data: dashboard, isLoading } = useQuery({
		queryKey: ["dashboard", role, selectedBuildingId, selectedOrgId],
		queryFn: () => dashboardApi.getDashboardData(role, selectedBuildingId, selectedOrgId),
		enabled: role !== "manager",
	});

	if (role === "manager") {
		return (
			<ManagerDashboard
				onNavigate={(path, state) => navigate(path, { state: { fromDashboard: true, ...state } })}
			/>
		);
	}

	if (isLoading || !dashboard) {
		return (
			<div style={{ padding: 100, textAlign: "center" }}>
				<Spin size="large" />
			</div>
		);
	}

	const buildingOptions = [
		{ label: "Barcha binolar", value: "all" },
		...(dashboard.buildingItems || []).map((b: any) => ({
			label: b.name,
			value: b.id,
		})),
	];

	const orgOptions = [
		{ label: "Barcha tashkilotlar", value: "all" },
		...(dashboard.organizationItems || []).map((o: any) => ({
			label: o.name,
			value: o.id,
		})),
	];

	return (
		<div className="crm-dashboard">
			<div className="crm-page-head">
				<div className="crm-dashboard-heading">
					<h1>{isSuperAdmin ? "Tizim - umumiy panel" : "Bosh sahifa"}</h1>
					<div className="crm-dashboard-role-subtitle">
						{isSuperAdmin ? "Barcha tashkilotlar yig'masi" : "Unicorp Group - joriy holat"}
					</div>
				</div>
				<div className="crm-head-actions">
					<Select
						value={selectedBuildingId}
						onChange={setSelectedBuildingId}
						options={buildingOptions}
						style={{ minWidth: 160 }}
					/>
					{isSuperAdmin ? (
						<Select
							className="crm-org-select"
							value={selectedOrgId}
							onChange={(val) => {
								setSelectedOrgId(val);
								setSelectedBuildingId("all");
							}}
							options={orgOptions}
							style={{ minWidth: 180 }}
						/>
					) : null}
				</div>
			</div>

			<section className="crm-kpi-grid">
				{dashboard.kpis.map((kpi: any) => {
					let onClick;
					if (isSuperAdmin) {
						if (kpi.id === "total-orgs") {
							onClick = () => {
								if (selectedOrgId && selectedOrgId !== "all") {
									navigate(`/organizations/${selectedOrgId}`, { state: { fromDashboard: true } });
								} else {
									navigate("/organizations", { state: { fromDashboard: true } });
								}
							};
						} else if (kpi.id === "total-users") {
							onClick = () => {
								if (selectedOrgId && selectedOrgId !== "all") {
									navigate(`/users?orgId=${selectedOrgId}`, { state: { fromDashboard: true } });
								} else {
									navigate("/users", { state: { fromDashboard: true } });
								}
							};
						} else if (kpi.id === "total-buildings") {
							onClick = () => {
								if (selectedOrgId && selectedOrgId !== "all") {
									navigate(`/objects?orgId=${selectedOrgId}`, { state: { fromDashboard: true } });
								} else {
									navigate("/objects", { state: { fromDashboard: true } });
								}
							};
						} else if (kpi.id === "total-sales") {
							onClick = () => {
								if (selectedOrgId && selectedOrgId !== "all") {
									navigate(`/sales?orgId=${selectedOrgId}`, { state: { fromDashboard: true } });
								} else {
									navigate("/sales", { state: { fromDashboard: true } });
								}
							};
						}
					} else if (kpi.id === "monthly-sales" || kpi.id === "active-sales") {
						onClick = () => navigate("/sales", { state: { fromDashboard: true } });
					} else if (kpi.id === "monthly-revenue") {
						onClick = () =>
							navigate("/reports/payments", { state: { fromDashboard: true, thisMonth: true } });
					} else if (kpi.id === "collected-payments") {
						onClick = () => navigate("/reports/payments", { state: { fromDashboard: true } });
					}

					return <KpiCard key={kpi.id} kpi={kpi} onClick={onClick} />;
				})}
			</section>

			<section className="crm-grid crm-chart-grid">
				<div
					className="crm-card"
					style={{ cursor: "pointer" }}
					onClick={() => navigate("/reports/sales", { state: { fromDashboard: true } })}
				>
					<div className="crm-card-head">
						<div>
							<h3>Binolar kesimida daromad</h3>
							<p>Kirim va reja bo'yicha solishtirma</p>
						</div>
						<div className="crm-seg">
							<button
								className={chartPeriod === "Yil" ? "active" : ""}
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									setChartPeriod("Yil");
								}}
							>
								Yil
							</button>
							<button
								className={chartPeriod === "Oy" ? "active" : ""}
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									setChartPeriod("Oy");
								}}
							>
								Oy
							</button>
						</div>
					</div>
					<div className="crm-card-body">
						<BarChart
							data={
								chartPeriod === "Yil"
									? dashboard.revenueByMonth
									: dashboard.revenueByWeek || dashboard.revenueByMonth
							}
							onBarClick={(label) =>
								navigate("/reports/sales", { state: { fromDashboard: true, filter: label } })
							}
						/>
						<div className="crm-legend">
							<span className="blue" />
							Kirim (mlrd)
						</div>
					</div>
				</div>

				<div className="crm-card">
					<div
						className="crm-card-head"
						style={{ cursor: "pointer" }}
						onClick={() => navigate("/reports/rooms", { state: { fromDashboard: true } })}
					>
						<h3>Xonalar holati</h3>
					</div>
					<div className="crm-card-body">
						<RoomsStatusDonut
							centerLabel="jami xona"
							centerValue={dashboard.roomStatuses
								.reduce((acc: number, curr: any) => acc + curr.value, 0)
								.toString()}
							items={dashboard.roomStatuses}
							onItemClick={(label) => {
								const statusKey = label.toLowerCase();
								const params = new URLSearchParams();
								params.set("status", statusKey);
								if (isSuperAdmin && selectedOrgId && selectedOrgId !== "all") {
									params.set("orgId", selectedOrgId);
								}
								if (selectedBuildingId && selectedBuildingId !== "all") {
									params.set("buildingId", selectedBuildingId);
								}
								navigate(`/rooms?${params.toString()}`, {
									state: { returnTo: "/dashboard" },
								});
							}}
							onCenterClick={() => navigate("/reports/rooms", { state: { fromDashboard: true } })}
						/>
					</div>
				</div>
			</section>

			<section
				className="crm-card crm-mt"
				style={{ cursor: "pointer" }}
				onClick={() => navigate("/reports/payments", { state: { fromDashboard: true } })}
			>
				<div className="crm-card-head">
					<div>
						<h3>Oylik dinamika</h3>
						<p>So'nggi 6 oy: yig'ilgan summa va reja</p>
					</div>
				</div>
				<div className="crm-card-body">
					<LineChart
						data={dashboard.monthlyDynamics}
						onPointClick={(label) =>
							navigate("/reports/payments", { state: { fromDashboard: true, filter: label } })
						}
					/>
					<div className="crm-legend-row">
						<span>
							<i className="blue" />
							Yig'ilgan
						</span>
						<span>
							<i className="slate" />
							Reja
						</span>
					</div>
				</div>
			</section>

			<section className="crm-grid crm-bottom-grid">
				<ManagersCard managers={dashboard.managers} />
				<PaymentDeadlinesCard payments={dashboard.payments} />
				<RecentSalesCard sales={dashboard.recentSales} />
			</section>
		</div>
	);
}

type ManagerDashboardProps = {
	onNavigate: (path: string, state?: any) => void;
};

function ManagerDashboard({ onNavigate }: ManagerDashboardProps) {
	const user = useAuthStore((state) => state.user);
	const { data, isLoading } = useQuery({
		queryKey: ["manager-dashboard", user?.id],
		queryFn: () => dashboardApi.getManagerDashboardData(user?.id || ""),
		enabled: !!user?.id,
	});

	if (isLoading || !data) {
		return (
			<div style={{ padding: 100, textAlign: "center" }}>
				<Spin size="large" />
			</div>
		);
	}

	const { kpis: managerKpis, sales: managerSales } = data;
	return (
		<div className="crm-dashboard crm-manager-dashboard">
			<div className="crm-page-head">
				<div>
					<h1>Bosh sahifa</h1>
					<p>{user?.fullName || "Menejer"} - shaxsiy panel</p>
				</div>
				<Button
					className="crm-primary-btn"
					icon={<PlusOutlined />}
					onClick={() => onNavigate("/sales/new")}
					type="primary"
				>
					Yangi sotuv
				</Button>
			</div>

			<section className="crm-kpi-grid crm-manager-kpis">
				{managerKpis.map((kpi: any) => {
					let onClick;
					if (kpi.id === "my-sales" || kpi.id === "this-month") {
						onClick = () => onNavigate("/sales", { manager: "Sardor T." });
					} else if (kpi.id === "total-amount" || kpi.id === "collected") {
						onClick = () => onNavigate("/payments");
					}

					return <KpiCard key={kpi.id} kpi={kpi} onClick={onClick} />;
				})}
			</section>

			<section className="crm-grid crm-manager-work-grid">
				<div className="crm-card crm-manager-actions-card">
					<div className="crm-card-head">
						<h3>Tezkor amallar</h3>
					</div>
					<div className="crm-card-body">
						<Button
							className="crm-primary-btn"
							icon={<PlusOutlined />}
							onClick={() => onNavigate("/sales/new")}
							type="primary"
						>
							Yangi sotuv
						</Button>
						<Button icon={<UserAddOutlined />} onClick={() => onNavigate("/clients/new")}>
							Mijoz qo'shish
						</Button>
						<Button icon={<CreditCardOutlined />} onClick={() => onNavigate("/payments/new")}>
							To'lov kiritish
						</Button>
						<Button icon={<MessageOutlined />} onClick={() => onNavigate("/sms/send")}>
							SMS yuborish
						</Button>
					</div>
				</div>

				<div className="crm-card crm-manager-tasks-card">
					<div className="crm-card-head">
						<h3>Bugungi vazifalar</h3>
					</div>
					<div className="crm-card-body">
						<div
							className="crm-manager-task-row"
							style={{ cursor: "pointer", transition: "background 0.2s" }}
							onClick={() => onNavigate("/payments", { date: "2026-06-09" })}
							onMouseEnter={(e) => (e.currentTarget.style.background = "var(--crm-surface-2)")}
							onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						>
							<span className="crm-badge amber">
								<ClockCircleOutlined />
								Bugun
							</span>
							<p>3 mijozdan to'lov kutilmoqda</p>
							<strong>58 000 000 UZS</strong>
						</div>
						<div
							className="crm-manager-task-row"
							style={{ cursor: "pointer", transition: "background 0.2s" }}
							onClick={() => onNavigate("/reports/debts", { minDelay: 1 })}
							onMouseEnter={(e) => (e.currentTarget.style.background = "var(--crm-surface-2)")}
							onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						>
							<span className="crm-badge red">
								<AlertOutlined />
								Kechikkan
							</span>
							<p>2 mijoz qarzdor</p>
							<strong>39 000 000 UZS</strong>
						</div>
						<div
							className="crm-manager-task-row"
							style={{ cursor: "pointer", transition: "background 0.2s" }}
							onClick={() => onNavigate("/clients")}
							onMouseEnter={(e) => (e.currentTarget.style.background = "var(--crm-surface-2)")}
							onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
						>
							<span className="crm-badge sky">
								<ClockCircleOutlined />
								Eslatma
							</span>
							<p>Mukhlis P. - qo'ng'iroq qilish</p>
						</div>
					</div>
				</div>
			</section>

			<section className="crm-card crm-table-card crm-manager-sales-card">
				<div className="crm-card-head">
					<h3>Mening so'nggi sotuvlarim</h3>
					<button onClick={() => onNavigate("/sales", { manager: "Sardor T." })} type="button">
						Barchasi
					</button>
				</div>
				<div className="crm-table-wrap">
					<table className="crm-table crm-manager-sales-table">
						<thead>
							<tr>
								<th>Mijoz</th>
								<th>Xona</th>
								<th>Bino</th>
								<th>Summa</th>
								<th>To'langan</th>
								<th>Holat</th>
							</tr>
						</thead>
						<tbody>
							{managerSales.map((sale: any) => (
								<tr
									key={sale.id}
									onClick={() => onNavigate(`/sales/${sale.id}`)}
									style={{ cursor: "pointer" }}
								>
									<td>
										<div className="crm-manager-client-cell">
											<span className={`crm-mini-avatar ${sale.tone}`}>{sale.initials}</span>
											<strong>{sale.client}</strong>
										</div>
									</td>
									<td className="num">{sale.room}</td>
									<td>{sale.building}</td>
									<td className="num">{sale.amount}</td>
									<td>
										<div className="crm-manager-progress">
											<span>
												<i style={{ width: `${sale.paid}%` }} />
											</span>
											<strong>{sale.paid}%</strong>
										</div>
									</td>
									<td>
										<span className="crm-status-badge green">
											<span />
											{sale.status}
										</span>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
}
